'use client';
import {Box} from '@mui/material';
import Container from '@mui/material/Container';
import {styled} from '@mui/material/styles';
import NextImage from 'next/image';
import NextLink from 'next/link';
import {sanitizeRichText} from '../../shared/utils/sanitize';
import {Button, externalLinkProps, isExternalHref, Section, Typography} from '../primitives';

// Figma "Featured projects section" (node 1:13960): a text column (heading +
// body paragraphs + outlined pill CTA) paired with a 450px circular photo. The
// circle can sit on either inline edge and mirrors with RTL. Stacks vertically
// (photo above text) on mobile — hence "vertical section".
const CIRCLE_DESKTOP = 450;
const BODY_MAX_WIDTH = 560;

const HTML_TAG_PATTERN = /<\/?[a-z][^>]*>/i;

export interface ProjectVerticalSectionAction {
  label: string;
  href: string;
  onClick?: () => void;
}

export interface ProjectVerticalSectionProps {
  title: string;
  paragraphs: string[];
  image: string;
  imageAlt?: string;
  mediaSide?: 'start' | 'end';
  action?: ProjectVerticalSectionAction;
  tone?: 'default' | 'subtle';
}

// DOM order is always Info → Media. On desktop, 'row' leaves Media at the
// inline-end (right in LTR) and 'row-reverse' moves it to the inline-start;
// because the row inherits the document `dir`, both flip automatically in RTL.
// On mobile the section stacks the circle above the text regardless of side.
const Row = styled(Box, {shouldForwardProp: propName => propName !== '$side'})<{$side: 'start' | 'end'}>(({theme, $side}) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(2),
  width: '100%',
  [theme.breakpoints.up('md')]: {
    flexDirection: $side === 'start' ? 'row-reverse' : 'row',
    justifyContent: 'space-between',
    gap: theme.spacing(6),
  },
}));

// [MASTER] Showcase Section mobile: title, circle, centred copy, then the button.
// Info and Description flatten into the Row there so the circle can sit between
// the title and the copy without duplicating either.
const Info = styled(Box)(({theme}) => ({
  display: 'contents',
  '& > a': {order: 3},
  [theme.breakpoints.up('md')]: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(2),
  },
}));

const Description = styled(Box)(({theme}) => ({
  display: 'contents',
  textAlign: 'center',
  '& > :not(:first-child)': {order: 2},
  [theme.breakpoints.up('md')]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.75),
    textAlign: 'start',
  },
}));

const Prose = styled(Box)(({theme}) => {
  const body = {fontSize: theme.font.size['body-2'], fontWeight: theme.font.weight.regular, lineHeight: theme.font.lineHeight['24']};
  return {
    color: theme.palette.colors.text.neutral.default,
    fontFamily: theme.typography.fontFamily,
    ...body,
    '& p': {margin: 0},
    '& p + p': {marginTop: theme.spacing(0.75)},
    '& strong, & b': {fontWeight: theme.typography.fontWeightBold},
    '& em, & i': {fontStyle: 'italic'},
    '& ul, & ol': {
      margin: 0,
      paddingInlineStart: theme.spacing(3),
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(0.75),
    },
    '& a': {
      color: theme.palette.colors.text.primary.default,
      textDecoration: 'underline',
      textUnderlineOffset: 2,
    },
    [theme.breakpoints.up('md')]: {maxWidth: BODY_MAX_WIDTH},
  };
});

const Media = styled(Box)(({theme}) => ({
  position: 'relative',
  flexShrink: 0,
  width: 250,
  order: 1,
  aspectRatio: '1 / 1',
  borderRadius: theme.radii.full,
  overflow: 'hidden',
  backgroundColor: theme.palette.colors.surface.neutral.subtle,
  [theme.breakpoints.up('md')]: {
    // Width only — aspectRatio above already squares it, and a paired height would
    // override that and let the circle skew if the width ever becomes fluid.
    width: CIRCLE_DESKTOP,
  },
}));

// [MASTER] Showcase Section: Contained Light on desktop, Outlined Dark on mobile.
const ACTION_VARIANTS = [
  {variant: 'contained-light', display: {xs: 'none', md: 'inline-flex'}},
  {variant: 'outlined-dark', display: {md: 'none'}},
] as const;

export function ProjectVerticalSection({
  title,
  paragraphs,
  image,
  imageAlt,
  mediaSide = 'end',
  action,
  tone = 'default',
}: ProjectVerticalSectionProps) {
  return (
    <Section tone={tone === 'subtle' ? 'subtle' : 'base'}>
      <Container>
        <Row $side={mediaSide}>
          <Info>
            <Description>
              <Typography
                size="title-2"
                weight="bold"
                mobileSize="title-5"
                mobileWeight="bold"
                as="h2"
                sx={{color: theme => theme.palette.colors.text.neutral.strong, textTransform: 'capitalize'}}
              >
                {title}
              </Typography>
              {paragraphs.map((paragraph, index) =>
                HTML_TAG_PATTERN.test(paragraph) ? (
                  <Prose key={index} dangerouslySetInnerHTML={{__html: sanitizeRichText(paragraph)}} />
                ) : (
                  <Typography
                    key={index}
                    size="body-2"
                    weight="regular"
                    lineHeight="24"
                    as="p"
                    sx={{color: theme => theme.palette.colors.text.neutral.default, maxWidth: {md: BODY_MAX_WIDTH}}}
                  >
                    {paragraph}
                  </Typography>
                ),
              )}
            </Description>
            {action &&
              ACTION_VARIANTS.map(({variant, display}) =>
                isExternalHref(action.href) ? (
                  <Button
                    key={variant}
                    variant={variant}
                    component="a"
                    href={action.href}
                    onClick={action.onClick}
                    sx={{display}}
                    {...externalLinkProps(action.href)}
                  >
                    {action.label}
                  </Button>
                ) : (
                  <Button key={variant} variant={variant} component={NextLink} href={action.href} onClick={action.onClick} sx={{display}}>
                    {action.label}
                  </Button>
                ),
              )}
          </Info>
          <Media>
            <NextImage
              src={image}
              alt={imageAlt ?? ''}
              fill
              sizes={`(max-width: 900px) 70vw, ${CIRCLE_DESKTOP}px`}
              style={{objectFit: 'cover'}}
            />
          </Media>
        </Row>
      </Container>
    </Section>
  );
}

ProjectVerticalSection.displayName = 'ProjectVerticalSection';
